import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
  type RefObject,
} from 'react';

const PULL_THRESHOLD_PX = 18;
const RELEASE_THRESHOLD_PX = 72;
const MAX_PULL_PX = 104;
const REFRESH_RESET_TIMEOUT_MS = 1600;
const INTERACTIVE_TARGET_SELECTOR =
  'a,button,input,textarea,select,label,[role="button"],[contenteditable=""],[contenteditable="true"],[data-ptr-ignore]';

type RegisteredSurfaceKey = 'tab-shell' | 'reader';
type IndicatorState = 'hidden' | 'pull' | 'release' | 'loading';

type RegisteredSurfaceMap = Partial<Record<RegisteredSurfaceKey, HTMLElement>>;

interface PullToRefreshProviderProps extends PropsWithChildren {
  pathname: string;
}

interface PullToRefreshContextValue {
  registerSurface: (surfaceKey: RegisteredSurfaceKey, element: HTMLElement | null) => void;
  unregisterSurface: (surfaceKey: RegisteredSurfaceKey, element: HTMLElement | null) => void;
}

interface ActiveSurface {
  element: HTMLElement;
  kind: RegisteredSurfaceKey | 'document';
}

interface GestureState {
  activeSurface: ActiveSurface;
  startX: number;
  startY: number;
  tracking: boolean;
}

const PullToRefreshContext = createContext<PullToRefreshContextValue | null>(null);

function getTouchPoint(event: TouchEvent) {
  const touch = event.touches[0] ?? event.changedTouches[0];
  if (!touch) return null;

  return {
    clientX: touch.clientX,
    clientY: touch.clientY,
  };
}

function applyPullResistance(distancePx: number) {
  const softenedDistance = distancePx * 0.45;
  return Math.max(0, Math.min(MAX_PULL_PX, Math.round(softenedDistance)));
}

function isRegisteredSurfaceKey(value: string): value is RegisteredSurfaceKey {
  return value === 'tab-shell' || value === 'reader';
}

function isInteractiveTarget(target: EventTarget | null) {
  return target instanceof Element && target.closest(INTERACTIVE_TARGET_SELECTOR) !== null;
}

function isNodeWithinActiveSurface(activeSurface: ActiveSurface, target: EventTarget | null) {
  if (!(target instanceof Node)) {
    return false;
  }

  if (activeSurface.kind === 'document') {
    return true;
  }

  return activeSurface.element.contains(target);
}

function getDocumentScrollSurface() {
  const scrollingElement = document.scrollingElement;

  if (scrollingElement instanceof HTMLElement) {
    return scrollingElement;
  }

  if (document.documentElement instanceof HTMLElement) {
    return document.documentElement;
  }

  return document.body instanceof HTMLElement ? document.body : null;
}

function getDocumentScrollTop() {
  const scrollTops = [document.scrollingElement, document.documentElement, document.body]
    .filter((element): element is HTMLElement => element instanceof HTMLElement)
    .map((element) => element.scrollTop);

  return scrollTops.length > 0 ? Math.max(...scrollTops) : 0;
}

function resolveActiveSurface(pathname: string, registeredSurfaces: RegisteredSurfaceMap): ActiveSurface | null {
  if (pathname.startsWith('/reader/')) {
    const readerSurface = registeredSurfaces.reader;
    if (readerSurface?.isConnected) {
      return { element: readerSurface, kind: 'reader' };
    }

    return null;
  }

  if (pathname === '/catalog' || pathname === '/my-clubs' || pathname === '/profile') {
    const tabSurface = registeredSurfaces['tab-shell'];
    if (tabSurface?.isConnected) {
      return { element: tabSurface, kind: 'tab-shell' };
    }
  }

  const documentSurface = getDocumentScrollSurface();
  if (!documentSurface) {
    return null;
  }

  return { element: documentSurface, kind: 'document' };
}

function isSurfaceAtTop(activeSurface: ActiveSurface) {
  if (activeSurface.kind === 'document') {
    return getDocumentScrollTop() <= 0;
  }

  return activeSurface.element.scrollTop <= 0 && getDocumentScrollTop() <= 0;
}

function getIndicatorCopy(indicatorState: IndicatorState) {
  switch (indicatorState) {
    case 'pull':
      return 'Потяните, чтобы обновить';
    case 'release':
      return 'Отпустите, чтобы обновить';
    case 'loading':
      return 'Обновляем…';
    case 'hidden':
    default:
      return '';
  }
}

export function isInstalledStandalonePwa() {
  if (typeof window === 'undefined') {
    return false;
  }

  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
  const matchesStandaloneMode = window.matchMedia?.('(display-mode: standalone)').matches ?? false;

  return matchesStandaloneMode || navigatorWithStandalone.standalone === true;
}

export function isPullToRefreshPathname(pathname: string) {
  return (
    pathname === '/catalog'
    || pathname === '/my-clubs'
    || pathname === '/profile'
    || /^\/clubs\/[^/]+$/.test(pathname)
    || /^\/reader\/[^/]+\/[^/]+$/.test(pathname)
    || /^\/paywall\/[^/]+$/.test(pathname)
  );
}

export const pullToRefreshRuntime = {
  reload() {
    window.location.reload();
  },
};

export function usePullToRefreshSurface(
  surfaceKey: RegisteredSurfaceKey,
  surfaceRef: RefObject<HTMLElement | null>,
) {
  const context = useContext(PullToRefreshContext);
  const registeredElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!context) {
      return;
    }

    const nextElement = surfaceRef.current;
    const registeredElement = registeredElementRef.current;

    if (registeredElement === nextElement) {
      return;
    }

    if (registeredElement) {
      context.unregisterSurface(surfaceKey, registeredElement);
    }

    registeredElementRef.current = nextElement;

    if (nextElement) {
      context.registerSurface(surfaceKey, nextElement);
    }
  });

  useEffect(() => {
    if (!context) {
      return;
    }

    return () => {
      const registeredElement = registeredElementRef.current;

      if (!registeredElement) {
        return;
      }

      context.unregisterSurface(surfaceKey, registeredElement);
      registeredElementRef.current = null;
    };
  }, [context, surfaceKey]);
}

export function PullToRefreshProvider({
  children,
  pathname,
}: PullToRefreshProviderProps) {
  const registeredSurfacesRef = useRef<RegisteredSurfaceMap>({});
  const refreshResetTimeoutRef = useRef<number | null>(null);
  const gestureStateRef = useRef<GestureState | null>(null);
  const refreshInFlightRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const [pullDistancePx, setPullDistancePx] = useState(0);
  const [indicatorState, setIndicatorState] = useState<IndicatorState>('hidden');

  function clearRefreshResetTimeout() {
    if (refreshResetTimeoutRef.current === null) {
      return;
    }

    window.clearTimeout(refreshResetTimeoutRef.current);
    refreshResetTimeoutRef.current = null;
  }

  function hideIndicator() {
    pullDistanceRef.current = 0;
    setPullDistancePx(0);

    if (!refreshInFlightRef.current) {
      setIndicatorState('hidden');
    }
  }

  function setPullDistance(distancePx: number) {
    pullDistanceRef.current = distancePx;
    setPullDistancePx(distancePx);
  }

  function registerSurface(surfaceKey: RegisteredSurfaceKey, element: HTMLElement | null) {
    if (!element) {
      return;
    }

    registeredSurfacesRef.current[surfaceKey] = element;
  }

  function unregisterSurface(surfaceKey: RegisteredSurfaceKey, element: HTMLElement | null) {
    const registeredElement = registeredSurfacesRef.current[surfaceKey];

    if (element && registeredElement && registeredElement !== element) {
      return;
    }

    delete registeredSurfacesRef.current[surfaceKey];
  }

  useEffect(() => {
    if (!isPullToRefreshPathname(pathname)) {
      gestureStateRef.current = null;
      refreshInFlightRef.current = false;
      clearRefreshResetTimeout();
      hideIndicator();
    }
  }, [pathname]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    function handleTouchStart(event: TouchEvent) {
      if (!isInstalledStandalonePwa() || !isPullToRefreshPathname(pathname) || refreshInFlightRef.current) {
        gestureStateRef.current = null;
        return;
      }

      const point = getTouchPoint(event);
      if (!point || isInteractiveTarget(event.target)) {
        gestureStateRef.current = null;
        return;
      }

      const activeSurface = resolveActiveSurface(pathname, registeredSurfacesRef.current);
      if (
        !activeSurface
        || !isSurfaceAtTop(activeSurface)
        || !isNodeWithinActiveSurface(activeSurface, event.target)
      ) {
        gestureStateRef.current = null;
        return;
      }

      gestureStateRef.current = {
        activeSurface,
        startX: point.clientX,
        startY: point.clientY,
        tracking: false,
      };
    }

    function handleTouchMove(event: TouchEvent) {
      const gestureState = gestureStateRef.current;
      if (!gestureState || refreshInFlightRef.current) {
        return;
      }

      const point = getTouchPoint(event);
      if (!point) {
        gestureStateRef.current = null;
        hideIndicator();
        return;
      }

      const deltaX = point.clientX - gestureState.startX;
      const deltaY = point.clientY - gestureState.startY;

      if (deltaY <= 0) {
        gestureStateRef.current = null;
        hideIndicator();
        return;
      }

      if (Math.abs(deltaX) > deltaY) {
        gestureStateRef.current = null;
        hideIndicator();
        return;
      }

      if (!isSurfaceAtTop(gestureState.activeSurface)) {
        gestureStateRef.current = null;
        hideIndicator();
        return;
      }

      const nextPullDistancePx = applyPullResistance(deltaY);

      if (nextPullDistancePx < PULL_THRESHOLD_PX) {
        return;
      }

      gestureState.tracking = true;
      event.preventDefault();
      setPullDistance(nextPullDistancePx);
      setIndicatorState(nextPullDistancePx >= RELEASE_THRESHOLD_PX ? 'release' : 'pull');
    }

    function handleTouchEnd() {
      const gestureState = gestureStateRef.current;
      gestureStateRef.current = null;

      if (!gestureState?.tracking) {
        hideIndicator();
        return;
      }

      if (pullDistanceRef.current < RELEASE_THRESHOLD_PX || refreshInFlightRef.current) {
        hideIndicator();
        return;
      }

      refreshInFlightRef.current = true;
      clearRefreshResetTimeout();
      setPullDistance(RELEASE_THRESHOLD_PX);
      setIndicatorState('loading');

      refreshResetTimeoutRef.current = window.setTimeout(() => {
        refreshInFlightRef.current = false;
        hideIndicator();
      }, REFRESH_RESET_TIMEOUT_MS);

      pullToRefreshRuntime.reload();
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [pathname]);

  useEffect(() => {
    return () => {
      clearRefreshResetTimeout();
    };
  }, []);

  return (
    <PullToRefreshContext.Provider value={{ registerSurface, unregisterSurface }}>
      {children}
      <div
        aria-hidden={indicatorState === 'hidden'}
        data-testid="pwa-pull-to-refresh-indicator"
        data-ptr-state={indicatorState}
        className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center"
        style={{
          opacity: indicatorState === 'hidden' ? 0 : 1,
          transform: `translateY(${indicatorState === 'hidden' ? -56 : Math.max(12, pullDistancePx)}px)`,
          transition: indicatorState === 'loading' || indicatorState === 'hidden' ? 'transform 180ms ease, opacity 180ms ease' : 'none',
        }}
      >
        <div className="mt-[calc(var(--safe-area-top)+0.5rem)] inline-flex min-h-11 items-center gap-2 rounded-full border border-[rgba(26,26,26,0.08)] bg-[rgba(255,253,248,0.96)] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgba(26,26,26,0.64)] shadow-[0_14px_28px_rgba(62,48,23,0.12)]">
          <span
            aria-hidden="true"
            className={[
              'inline-flex h-6 w-6 items-center justify-center rounded-full bg-[rgba(232,93,42,0.12)] text-[var(--accent)] transition-transform duration-150',
              indicatorState === 'release' ? 'rotate-180' : '',
              indicatorState === 'loading' ? 'animate-pulse' : '',
            ].join(' ')}
          >
            ↓
          </span>
          <span>{getIndicatorCopy(indicatorState)}</span>
        </div>
      </div>
    </PullToRefreshContext.Provider>
  );
}

export function getPullToRefreshSurfaceKey(value: string | null) {
  if (!value || !isRegisteredSurfaceKey(value)) {
    return null;
  }

  return value;
}
